import Route from '@ember/routing/route';
import type Router from '@ember/routing/router';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import '@glint/environment-ember-loose';
import logger from 'donor-elf-web/lib/logger';
import type CurrentUser from 'donor-elf-web/services/current-user';
import type NavbarSettingsService from 'donor-elf-web/services/navbar-settings';
import type SessionService from 'donor-elf-web/services/session';

export default class ApplicationRoute extends Route {
  @service declare currentUser: CurrentUser;
  @service declare navbarSettings: NavbarSettingsService;
  @service declare router: Router;
  @service declare session: SessionService;

  queryParams = {
    loginToken: {
      replace: true,
    },
    loginEmail: {
      replace: true,
    },
  };

  async beforeModel(transition: Transition) {
    // from the ember-simple-auth sample application
    await this.session.setup();

    if (this.session.isAuthenticated) {
      // Try to load the current user, but it may fail if the session is
      // still authenticated but the user cookie is expired.  We don't
      // need to hear about it.
      try {
        await this.loadCurrentUser();
      } catch {
        await this.invalidateSession();
      }
    }

    this.navbarSettings.title = 'DonorElf';

    const params = this.paramsFor('application') as any;

    if (isPresent(params.loginToken)) {
      await this.handleLoginToken(params, transition);
    } else if (isPresent(params.loginEmail)) {
      await this.handleLoginEmail(params, transition);
    }
  }

  async handleLoginEmail(params: any, transition: Transition) {
    if (
      this.session.isAuthenticated &&
      this.currentUser?.email !== params.loginEmail
    ) {
      transition.abort();

      this.invalidateSession();
      return;
    }
  }

  async handleLoginToken(params: any, transition: Transition) {
    if (this.session.isAuthenticated) {
      transition.abort();

      this.invalidateSession();
      return;
    }

    try {
      await this.session.authenticate(
        'authenticator:oauth2',
        'login_token',
        params.loginToken,
      );

      await this.loadCurrentUser();
    } catch (error: any) {
      logger.error('application: Error authenticating with login token', error);
    }
  }

  async invalidateSession() {
    await this.session.invalidate();

    // we need to reload the page to confirm we clear all the
    // data from the previous login
    window.location.reload();
  }

  async loadCurrentUser() {
    try {
      await this.currentUser.load();
    } catch (error: any) {
      logger.error('application: Error loading current user', error);

      if (this.session.isAuthenticated) {
        await this.invalidateSession();
      }
    }
  }

  redirect(model: any, transition: Transition) {
    const {
      name,
      queryParams: { loginToken },
    } = transition.to as any;

    if (
      name?.startsWith('login') &&
      loginToken &&
      this.session.isAuthenticated
    ) {
      // if the route is login, we want to redirect to the dashboard
      this.router.transitionTo('funds.fund', 'selected-fund');
    }
  }
}
